import { useEffect, useRef, useState } from 'react';

type MediaQueryCallback = (event: { matches: boolean; media: string }) => void;

function attachMediaListener(query: MediaQueryList, callback: MediaQueryCallback) {
  query.addEventListener('change', callback);
  return () => query.removeEventListener('change', callback);
}

function getInitialValue(query: string, initialValue?: boolean) {
  if (typeof initialValue === 'boolean') {
    return initialValue;
  }

  if (typeof window !== 'undefined' && 'matchMedia' in window) {
    return window.matchMedia(query).matches;
  }

  return false;
}

export interface UseMediaQueryOptions {
  getInitialValueInEffect: boolean;
}

export function useMediaQuery(
  query: string,
  initialValue?: boolean,
  { getInitialValueInEffect }: UseMediaQueryOptions = {
    getInitialValueInEffect: true,
  },
) {
  const [matches, setMatches] = useState(getInitialValueInEffect ? initialValue : getInitialValue(query));

  const queryRef = useRef<MediaQueryList>(null);

  useEffect(() => {
    if ('matchMedia' in window) {
      queryRef.current = window.matchMedia(query);
      setMatches(queryRef.current.matches);
      return attachMediaListener(queryRef.current, (event) => setMatches(event.matches));
    }
    return;
  }, [query]);

  return matches;
}

// The logic here is that we want there to be a line between "mobile" and "desktop" at 1024px.
// Anything 1024px and BELOW is considered mobile/tablet, anything ABOVE is desktop.
export enum Breakpoints {
  Mobile = '(max-width: 767px)',
  Tablet = '(max-width: 1024px)',
  Handheld = '(max-width: 1024px)',
  NonHandheld = '(min-width: 1025px)',
  SmallScreens = '(min-width: 1025px)',
  Desktop = '(min-width: 1280px)',
  LargeDesktop = '(min-width: 1440px)',
}

export enum DeviceSize {
  Mobile = 0,
  Tablet = 768,
  Laptop = 1025,
  SmallScreen = 1280,
  LargeDesktop = 1440,
}
